import React from 'react';
import AuthContainer from '../../../containers/AuthContainer';
import AccountSettingsContainer from '../../../containers/AccountSettingsContainer';
import ShoppingCartContainer from '../../../containers/ShoppingCartContainer';
import StoreFeedContainer from '../../../containers/StoreFeedContainer';
import BookmarksContainer from '../../../containers/BookmarksContainer';
import CheckoutContainer from '../../../containers/CheckoutContainer';
import OrderHistoryContainer from '../../../containers/OrderHistoryContainer';
import OrderContainer from '../../../containers/OrderContainer';
import CollectionsListContainer from '../../../containers/CollectionsListContainer';
import CollectionContainer from '../../../containers/CollectionContainer';
import InventoryRecordContainer from '../../../containers/InventoryRecordContainer';
import InventorySearchContainer from '../../../containers/InventorySearchContainer';
import CategoryContainer from '../../../containers/CategoryContainer';
import CategoryListContainer from '../../../containers/CategoryListContainer';
import MarketingIndexContainer from '../../../containers/MarketingIndexContainer';
import MarketingAboutContainer from '../../../containers/MarketingAboutContainer';
import MarketingPricingContainer from '../../../containers/MarketingPricingContainer';
import MarketingStoresListContainer from '../../../containers/MarketingStoresListContainer';
import UniversalSearchContainer from '../../../containers/UniversalSearchContainer';
import WineSkuContainer from '../../../containers/WineSkuContainer';
import Checkout from '../../../components/Checkout';
import StoreAboutPage from '../../../components/StoreAboutPage';
import Download from '../../../components/Download';
import DownloadBanner from '../../../components/DownloadBanner';
import OrderConfirmation from '../../../components/OrderConfirmation';
import Page from '../../../components/Page';
import CollectionConstants from '../../../constants/CollectionConstants';
import InventoryRecordConstants from '../../../constants/InventoryRecordConstants';
import StoreConstants from '../../../constants/StoreConstants';
import CustomerActions from '../../../actions/CustomerActions';
import BookmarkActions from '../../../actions/BookmarkActions';
import CollectionActions from '../../../actions/CollectionActions';
import InventoryRecordActions from '../../../actions/InventoryRecordActions';
import OrderActions from '../../../actions/OrderActions';
import StoreActions from '../../../actions/StoreActions';
import WineSkuActions from '../../../actions/WineSkuActions';
import RouteStore from '../../../stores/RouteStore';
import OrderStore from '../../../stores/OrderStore';

const isBrowser = (typeof window !== 'undefined');

export const RouteTypes = {
  Marketing: 'marketing',
  Embed: 'embed',
  StoreOptional: 'store_optional',
  StoreRequired: 'store_required'
};

export function redirect(url) {
  return () => {
    if(isBrowser) {
      return window.location = url;
    }
    let error = new Error();
    error.statusCode = 302;
    error.location = url;
    throw error;
  };
}

export function redirectIfNotSignedIn(storeSlug) {
  return (customer) => {
    if(!customer.isSignedIn()) {
      let url = `/sign-in`;
      if(storeSlug) {
        url = `/${storeSlug}/sign-in`;
      }
      return redirect(url)();
    }
    return customer;
  };
}

const Routes = [
  //
  // Static Routes
  //

  {
    name: 'home',
    handler: MarketingIndexContainer,
    type: RouteTypes.Marketing,
    segmentViewName: 'home',
    action: (context, route) => {
      context.dispatch(StoreConstants.SET_CURRENT_STORE, null);
      return Promise.all([
        context.executeAction(CustomerActions.findOrCreate, {}),
        context.executeAction(CustomerActions.loadDismissCookies, {}),
        context.executeAction(InventoryRecordActions.fetchStats, {})
      ]);
    }
  },
  {
    name: 'pricing',
    handler: MarketingPricingContainer,
    type: RouteTypes.Marketing,
    segmentViewName: 'pricing',
    action: (context, route) => {
      context.dispatch(StoreConstants.SET_CURRENT_STORE, null);
      return context.executeAction(CustomerActions.findOrCreate, {});
    }
  },
  {
    name: 'storesList',
    handler: MarketingStoresListContainer,
    type: RouteTypes.Marketing,
    segmentViewName: 'storesList',
    action: (context, route) => {
      context.dispatch(StoreConstants.SET_CURRENT_STORE, null);
      return Promise.all([
        context.executeAction(CustomerActions.findOrCreate, {}),
        context.executeAction(InventoryRecordActions.fetchStats, {}),
        context.executeAction(StoreActions.list, {
          pagination: {
            offset: 0,
            limit: 100
          }
        })
      ]);
    }
  },
  {
    name: 'download',
    handler: Download,
    type: RouteTypes.Marketing,
    segmentViewName: 'download',
    action: (context, route) => {
      return context.dispatch(StoreConstants.SET_CURRENT_STORE, null);
    }
  },
  {
    name: 'universalSearch',
    handler: UniversalSearchContainer,
    type: RouteTypes.Marketing,
    segmentViewName: 'universal_search',
    action: (context, route, baseDeps) => {
      return context.executeAction(WineSkuActions.list, {
        filters: route.query.filters || [],
        sorter: route.query.sorter
      });
    }
  },
  {
    name: 'wineSku',
    handler: WineSkuContainer,
    type: RouteTypes.Marketing,
    segmentViewName: 'universal_product_details',
    action: (context, route, baseDeps) => {
      return context.executeAction(WineSkuActions.fetch, {id: route.params.id});
    }
  },

  //
  // Dynamic StoreOptional Routes
  //

  {
    name: 'signIn',
    handler: AuthContainer,
    type: RouteTypes.StoreOptional,
    segmentViewName: 'auth_email_entry',
    action: (context, route, baseDeps) => {
      let { identifier_id, identifier_verification_code } = route.query;
      if(identifier_id && identifier_verification_code) {
        let prevRoute = context.getStore(RouteStore).getPreviousRoute();
        return context.executeAction(CustomerActions.auth, {identifier_id, identifier_verification_code})
          .then(redirect(prevRoute && prevRoute.url ? prevRoute.url : (context.getCurrentStore() ? context.getCurrentStore().root_path : '/')));
      }
      if(!context.getCurrentStore()) {
        return context.executeAction(CustomerActions.findOrCreate, {});
      }
      return null;
    }
  },
  {
    name: 'setPassword',
    handler: AuthContainer,
    type: RouteTypes.StoreOptional,
    segmentViewName: 'set_password',
    action: (context, route, baseDeps) => {
      if(!context.getCurrentStore()) {
        return context.executeAction(CustomerActions.findOrCreate, {}).then(redirectIfNotSignedIn());
      }
      return null;
    }
  },
  {
    name: 'activateAccount',
    handler: AuthContainer,
    type: RouteTypes.StoreOptional,
    segmentViewName: 'activate_account',
    action: (context, route, baseDeps) => {
      if(!context.getCurrentStore()) {
        return context.executeAction(CustomerActions.findOrCreate, {}).then(redirectIfNotSignedIn());
      }
      return null;
    }
  },
  {
    name: 'settings',
    handler: AccountSettingsContainer,
    type: RouteTypes.StoreOptional,
    segmentViewName: 'settings',
    action: (context, route, baseDeps) => {
      if(!context.getCurrentStore()) {
        return context.executeAction(CustomerActions.findOrCreate, {}).then(redirectIfNotSignedIn());
      }
      return null;
    }
  },
  {
    name: 'signOut',
    handler: 'div',
    type: RouteTypes.StoreOptional,
    segmentViewName: 'auth_sign_out',
    action: (context, route, baseDeps) => {
      let prevRoute = context.getStore(RouteStore).getPreviousRoute();
      return context.executeAction(CustomerActions.signOut, {})
        .then(redirect(prevRoute && prevRoute.url ? prevRoute.url : (context.getCurrentStore() ? context.getCurrentStore().root_path : '/')));
    }
  },

  //
  // Dynamic StoreRequired Routes
  //

  {
    name: 'storeLandingPage',
    handler: StoreFeedContainer,
    type: RouteTypes.StoreRequired,
    segmentViewName: 'store_landing',
    action: (context, route, baseDeps) => {
      return context.executeAction(StoreActions.fetchFeed, {slug: baseDeps.store.slug});
    }
  },
  {
    name: 'storeAboutPage',
    handler: StoreAboutPage,
    type: RouteTypes.StoreRequired,
    layoutOptions: {
      hideNavbar: true,
      hideFooter: true,
      hidePromoBanner: true
    },
    segmentViewName: 'store_about'
  },
  {
    name: 'refundPolicy',
    handler: (props) => {
      let pageProps = {
        header: 'Return & Refund Policy',
        text: props.store.refund_policy
      };
      return <Page {...pageProps} />;
    },
    type: RouteTypes.StoreRequired,
    segmentViewName: 'store_refund_policy',
    action: (context, route, baseDeps) => {
      if(!baseDeps.store.refund_policy) {
        return redirect(store.root_path)();
      }
      return null;
    }
  },
  {
    name: 'termsOfService',
    handler: (props) => {
      let pageProps = {
        header: 'Terms of Service',
        text: props.store.terms_of_service
      };
      return <Page {...pageProps} />;
    },
    type: RouteTypes.StoreRequired,
    segmentViewName: 'store_terms_of_service',
    action: (context, route, baseDeps) => {
      if(!baseDeps.store.terms_of_service) {
        return redirect(store.root_path)();
      }
      return null;
    }
  },
  {
    name: 'bag',
    handler: ShoppingCartContainer,
    layoutOptions: {hideFooter: true},
    type: RouteTypes.StoreRequired,
    segmentViewName: 'cart'
  },
  {
    name: 'saved',
    handler: BookmarksContainer,
    layoutOptions: {hideFooter: true},
    type: RouteTypes.StoreRequired,
    segmentViewName: 'saved',
    action: (context, route, baseDeps) => {
      return context.executeAction(BookmarkActions.list, {});
    }
  },
  {
    name: 'checkout',
    handler: CheckoutContainer,
    layoutOptions: {
      hideNavbar: true,
      hideFooter: true,
      hidePromoBanner: true
    },
    type: RouteTypes.StoreRequired,
    segmentViewName: 'checkout'
  },
  {
    name: 'orderConfirmation',
    handler: OrderConfirmation,
    layoutOptions: {
      hideNavbar: true,
      hideFooter: true,
      hidePromoBanner: true
    },
    type: RouteTypes.StoreRequired,
    segmentViewName: 'checkout_order_confirmation',
    action: (context, route, baseDeps) => {
      let hasCurrentOrder = !!context.getStore(OrderStore).getCurrentEntity();
      if(!hasCurrentOrder) {
        return redirect(context.getCurrentStore().root_path)();
      }
      return context.executeAction(CustomerActions.findOrCreate, {});
    }
  },
  {
    name: 'orderHistory',
    handler: OrderHistoryContainer,
    type: RouteTypes.StoreRequired,
    segmentViewName: 'order_history',
    action: (context, route, baseDeps) => {
      return context.executeAction(OrderActions.list, {});
    }
  },
  {
    name: 'order',
    handler: OrderContainer,
    type: RouteTypes.StoreRequired,
    segmentViewName: 'order_receipt',
    action: (context, route, baseDeps) => {
      return context.executeAction(OrderActions.fetch, {id: route.params.id});
    }
  },
  {
    name: 'collections',
    handler: CollectionsListContainer,
    type: RouteTypes.StoreRequired,
    segmentViewName: 'collections_list',
    action: (context, route, baseDeps) => {
      return context.executeAction(CollectionActions.list, {});
    }
  },
  {
    name: 'collection',
    handler: CollectionContainer,
    type: RouteTypes.StoreRequired,
    segmentViewName: 'collection',
    action: (context, route, baseDeps) => {
      let {slug} = route.params;
      context.dispatch(CollectionConstants.SET_CURRENT_COLLECTION, slug);
      return context.executeAction(CollectionActions.fetch, {
        slug: slug,
        store_id: baseDeps.store.id
      });
    }
  },
  {
    name: 'inventoryRecord',
    handler: InventoryRecordContainer,
    type: RouteTypes.StoreRequired,
    segmentViewName: 'product_details',
    action: (context, route, baseDeps) => {
      let {id} = route.params;
      context.dispatch(InventoryRecordConstants.FETCH_INVENTORY_RECORD_START, id);
      return context.executeAction(InventoryRecordActions.fetch, {id});
    }
  },
  {
    name: 'supplierInventoryRecord',
    handler: InventoryRecordContainer,
    type: RouteTypes.StoreRequired,
    segmentViewName: 'supplier_product_details',
    action: (context, route, baseDeps) => {
      let sku = route.params.sku;
      context.dispatch(InventoryRecordConstants.FETCH_INVENTORY_RECORD_START, sku);
      return context.executeAction(InventoryRecordActions.fetchBySupplierSku, {store_id: baseDeps.store.id, store_slug: baseDeps.store.slug, supplier_sku: sku});
    }
  },
  {
    name: 'collectionInventoryRecord',
    handler: InventoryRecordContainer,
    type: RouteTypes.StoreRequired,
    segmentViewName: 'collection_product_details',
    action: (context, route, baseDeps) => {
      let {collectionSlug, id} = route.params;
      context.dispatch(InventoryRecordConstants.FETCH_INVENTORY_RECORD_START, id);
      return Promise.all([
        context.executeAction(CollectionActions.fetch, {
          slug: collectionSlug,
          store_id: baseDeps.store.id
        }),
        context.executeAction(InventoryRecordActions.fetch, {id})
      ]);
    }
  },
  {
    name: 'categoryList',
    handler: CategoryListContainer,
    type: RouteTypes.StoreRequired,
    segmentViewName: 'category_list'
  },
  // Handle sparkling different (no sub-categories)
  {
    name: 'sparkling',
    handler: CategoryContainer,
    type: RouteTypes.StoreRequired,
    segmentViewName: 'category',
    action: (context, route, baseDeps) => {
      return context.executeAction(InventoryRecordActions.list, {
        filters: (route.query.filters || []).concat(route.params.filters),
        sorter: route.query.sorter || route.params.sorter
      });
    }
  },
  {
    name: 'category',
    handler: CategoryContainer,
    type: RouteTypes.StoreRequired,
    segmentViewName: 'category',
    action: (context, route, baseDeps) => {
      return context.executeAction(InventoryRecordActions.list, {
        filters: (route.query.filters || []).concat(route.params.filters),
        sorter: route.query.sorter || route.params.sorter
      });
    }
  },
  {
    name: 'search',
    handler: InventorySearchContainer,
    type: RouteTypes.StoreRequired,
    segmentViewName: 'search',
    action: (context, route, baseDeps) => {
      return context.executeAction(InventoryRecordActions.list, {
        filters: route.query.filters,
        sorter: route.query.sorter
      });
    }
  },
  //
  // Emebds Routes
  //
  {
    name: 'embeddedDownloadBanner',
    handler: DownloadBanner,
    type: RouteTypes.Embed,
    layoutOptions: {
      hideNavbar: true,
      hideFooter: true,
      hidePromoBanner: true
    },
    action: (context, route, done) => {
      let id = route.params.storeId;
      return context.executeAction(StoreActions.fetch, {id});
    }
  }
];

export default Routes;



// WEBPACK FOOTER //
// banquet/lib/BanquetRouteManager/lib/Routes.js